import React from "react";
import CONSOLE from "src/utils/console";
import Console from "src/utils/console";
import Login from "src/components/login";
import { ERROR_STORE } from "src/context/error-context";
import { RequiredError, ResponseError, SimpleUser } from "src/api/generated/frontend_user_v1";
import { ApiUser } from "src/api/api_user";

/** The global {@link UserMeProvider} instance */
let USERME_PROVIDER: UserMeProvider | null = null;

/** Data provided by the {@link USERME_CONTEXT} */
export type UserMeContext = {
    /** The currently logged-in account */
    user: SimpleUser;

    /** Reload the account's information */
    reset: () => void;
};

/** {@link React.Context} to access {@link SimpleUser account information} */
const USERME_CONTEXT = React.createContext<UserMeContext>({
    user: {
        display_name: "",
        uuid: "",
    },

    /**
     * Reset the account's information
     */
    reset: () => {},
});
USERME_CONTEXT.displayName = "UserMeContext";
export default USERME_CONTEXT;

/**
 * The properties of the account provider
 */
type UserMeProviderProps = {
    /** The children of the properties */
    children: React.ReactNode | Array<React.ReactNode>;
};

/**
 * The state of the account provider
 */
type UserMeProviderState = {
    /** The account */
    user: SimpleUser | "unauthenticated" | "loading";
};

/**
 * Component for managing and providing the {@link UserMeContext}
 *
 * This is a **singleton** only use at most **one** instance in your application.
 */
export class UserMeProvider extends React.Component<UserMeProviderProps, UserMeProviderState> {
    state: UserMeProviderState = { user: "loading" };

    fetching: boolean = false;

    /**
     * Fetch the account
     */
    fetchUser = async () => {
        // Guard against a lot of calls
        if (this.fetching) return;
        this.fetching = true;

        this.setState({ user: "loading" });

        try {
            const user = await ApiUser.auth.getMe();
            // dayjs.locale(user.preferred_lang.toLocaleLowerCase());
            this.setState({ user });
        } catch (e) {
            let msg;
            if (e instanceof ResponseError) {
                if (e.response.status === 401) {
                    this.setState({ user: "unauthenticated" });
                    return;
                }
                msg = e.response.statusText;
            } else if (e instanceof RequiredError) {
                Console.error(e);
                msg = "The server's response didn't match the spec";
            } else {
                Console.error("Unknown error occurred:", e);
                msg = "Unknown error occurred";
            }

            ERROR_STORE.report(msg);
            throw msg;
        } finally {
            // Clear guard against a lot of calls
            this.fetching = false;
        }
    };

    /**
     * Hook when the component mounts
     */
    componentDidMount() {
        this.fetchUser().then();

        // Register as global singleton
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        if (USERME_PROVIDER === null) USERME_PROVIDER = this;
        else if (USERME_PROVIDER === this) CONSOLE.error("UserProvider did mount twice");
        else CONSOLE.error("Two instances of UserProvider are used");
    }

    /**
     * Hook when the component will unmount
     */
    componentWillUnmount() {
        // Deregister as global singleton
        if (USERME_PROVIDER === this) USERME_PROVIDER = null;
        else if (USERME_PROVIDER === null) CONSOLE.error("UserProvider instance did unmount twice");
        else CONSOLE.error("Two instances of UserProvider are used");
    }

    /**
     * The render function
     *
     * @returns The rendered component
     */
    render() {
        switch (this.state.user) {
            case "loading":
                return <div></div>;
            case "unauthenticated":
                return (
                    <Login
                        onLogin={() => {
                            this.fetchUser();
                        }}
                    />
                );
            default:
                return (
                    <USERME_CONTEXT.Provider
                        value={{
                            user: this.state.user,
                            reset: this.fetchUser,
                        }}
                    >
                        {this.props.children}
                    </USERME_CONTEXT.Provider>
                );
        }
    }
}
